import { useState, useCallback } from 'react';

const DARK_BLUE_NODE_IDS = ['node-790-435', 'node-1040-385', 'node-320-280'];

interface NetworkRailIllustrationProps {
    alt?: string;
    className?: string;
    style?: React.CSSProperties;
}

export const NetworkRailIllustration = ({
    alt = 'Railway network illustration showing interconnected nodes',
    className,
    style,
}: NetworkRailIllustrationProps): JSX.Element => {
    const [clickedNodes, setClickedNodes] = useState<Set<string>>(new Set());
    const [animationKey, setAnimationKey] = useState(0);

    const allNodesClicked = DARK_BLUE_NODE_IDS.every(id => clickedNodes.has(id));

    const handleNodeClick = useCallback((nodeId: string) => {
        setClickedNodes(prev => {
            const allCurrentlyClicked = DARK_BLUE_NODE_IDS.every(id => prev.has(id));

            // If all nodes are already clicked, reset and start over
            if (allCurrentlyClicked) {
                const next = new Set<string>();
                next.add(nodeId);
                setAnimationKey(k => k + 1);
                return next;
            }

            // Otherwise, add the clicked node
            const next = new Set(prev);
            next.add(nodeId);

            // Check if this click completes the set
            if (DARK_BLUE_NODE_IDS.every(id => next.has(id))) {
                setAnimationKey(k => k + 1);
            }

            return next;
        });
    }, []);

    return (
        <svg
            width="100%"
            height="100%"
            viewBox="260 40 960 760"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label={alt}
            className={className}
            style={style}
        >
            <defs>
                {/* Soft background */}
                <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EAF3FF" />
                    <stop offset="100%" stopColor="#FFFFFF" />
                </linearGradient>

                {/* Clip path for circular badge */}
                <clipPath id="mask">
                    <circle cx="700" cy="400" r="360" />
                </clipPath>
            </defs>

            {/* Subtle shadow under "badge" */}
            <ellipse cx="700" cy="760" rx="290" ry="18" fill="#000" opacity="0.06" />

            {/* Background */}
            <circle cx="700" cy="400" r="360" fill="url(#sky)" />

            {/* Landscape and rails (inside circle) */}
            <g clipPath="url(#mask)">
                {/* Landscape */}
                <path
                    d="M0,510 C260,470 520,505 780,480 C1030,455 1230,480 1400,505 L1400,800 L0,800 Z"
                    fill="#A8D39A"
                    opacity="0.85"
                />
                <path
                    d="M0,470 C240,430 520,465 820,440 C1090,420 1280,445 1400,460 L1400,800 L0,800 Z"
                    fill="#9CCC93"
                    opacity="0.9"
                />

                {/* Rail layer */}
                <g transform="translate(0,610)">
                    <line x1="0" y1="0" x2="1400" y2="0" stroke="#0D2E57" strokeWidth="12" strokeLinecap="round" />
                    <line x1="0" y1="28" x2="1400" y2="28" stroke="#0D2E57" strokeWidth="12" strokeLinecap="round" />
                    {/* Sleepers */}
                    <g opacity="0.95">
                        {[20, 80, 140, 200, 260, 320, 380, 440, 500, 560, 620, 680, 740, 800, 860, 920, 980, 1040, 1100, 1160, 1220, 1280, 1340].map(x => (
                            <rect key={x} fill="#0D2E57" x={x} y="-12" width="36" height="70" rx="3" />
                        ))}
                    </g>
                </g>
            </g>

            {/* EDGES - drawn BEFORE nodes (z-order) */}
            <g id="edges" opacity="0.95">
                {/* Connections around a central core */}
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="700" y1="340" x2="580" y2="260" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="700" y1="340" x2="840" y2="270" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="700" y1="340" x2="610" y2="420" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="700" y1="340" x2="790" y2="435" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="700" y1="340" x2="900" y2="335" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="700" y1="340" x2="510" y2="340" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="700" y1="340" x2="670" y2="230" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="700" y1="340" x2="720" y2="470" />

                {/* Side connections (organic, but airy) */}
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="580" y1="260" x2="510" y2="340" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="580" y1="260" x2="670" y2="230" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="840" y1="270" x2="900" y2="335" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="610" y1="420" x2="510" y2="340" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="790" y1="435" x2="900" y2="335" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="610" y1="420" x2="720" y2="470" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="840" y1="270" x2="980" y2="300" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="980" y1="300" x2="1040" y2="385" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="510" y1="340" x2="430" y2="310" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="430" y1="310" x2="460" y2="430" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="460" y1="430" x2="610" y2="420" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="900" y1="335" x2="1040" y2="385" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="790" y1="435" x2="860" y2="520" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="720" y1="470" x2="860" y2="520" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="670" y1="230" x2="840" y2="270" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="580" y1="260" x2="670" y2="230" />

                {/* New connections to nodes outside the circle */}
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="1040" y1="385" x2="1120" y2="450" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="1040" y1="385" x2="1100" y2="300" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="980" y1="300" x2="1100" y2="300" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="1100" y1="300" x2="1180" y2="340" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="1120" y1="450" x2="1180" y2="340" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="430" y1="310" x2="320" y2="280" />
                <line stroke="#377BD4" strokeWidth="5" strokeLinecap="round" fill="none" x1="580" y1="260" x2="520" y2="150" />
            </g>

            {/* NODES - drawn AFTER edges (on top of everything) */}

            {/* Central core */}
            <circle cx="700" cy="340" r="34" stroke="#1D2667" strokeWidth="3" fill="rgba(255, 235, 173, 1)" />

            {/* Ring of nodes (organic, varying radius) */}
            <circle cx="580" cy="260" r="28" stroke="#1D2667" strokeWidth="3" fill="rgba(225, 243, 250, 1)" />
            <circle cx="840" cy="270" r="26" stroke="#1D2667" strokeWidth="3" fill="rgba(187, 249, 197, 1)" />
            <circle cx="610" cy="420" r="30" stroke="#1D2667" strokeWidth="3" fill="rgba(255, 221, 189, 1)" />

            {/* Dark blue node 1 - Interactive (secret) */}
            <circle
                cx="790"
                cy="435"
                r="30"
                stroke="#1D2667"
                strokeWidth="3"
                fill="rgba(0, 83, 166, 1)"
                onClick={() => handleNodeClick('node-790-435')}
                style={{ cursor: 'pointer' }}
            />

            <circle cx="900" cy="335" r="26" stroke="#1D2667" strokeWidth="3" fill="rgba(225, 243, 250, 1)" />
            <circle cx="510" cy="340" r="26" stroke="#1D2667" strokeWidth="3" fill="rgba(187, 249, 197, 1)" />
            <circle cx="670" cy="230" r="22" stroke="#1D2667" strokeWidth="3" fill="rgba(255, 221, 189, 1)" />
            <circle cx="720" cy="470" r="24" stroke="#1D2667" strokeWidth="3" fill="rgba(225, 243, 250, 1)" />

            {/* Satellites */}
            <circle cx="980" cy="300" r="22" stroke="#1D2667" strokeWidth="3" fill="rgba(255, 235, 173, 1)" />

            {/* Dark blue node 2 - Interactive (secret) */}
            <circle
                cx="1040"
                cy="385"
                r="26"
                stroke="#1D2667"
                strokeWidth="3"
                fill="rgba(0, 83, 166, 1)"
                onClick={() => handleNodeClick('node-1040-385')}
                style={{ cursor: 'pointer' }}
            />

            <circle cx="430" cy="310" r="24" stroke="#1D2667" strokeWidth="3" fill="rgba(225, 243, 250, 1)" />
            <circle cx="460" cy="430" r="26" stroke="#1D2667" strokeWidth="3" fill="rgba(187, 249, 197, 1)" />
            <circle cx="860" cy="520" r="24" stroke="#1D2667" strokeWidth="3" fill="rgba(255, 221, 189, 1)" />

            {/* New nodes outside the circle */}
            <circle cx="1120" cy="450" r="28" stroke="#1D2667" strokeWidth="3" fill="rgba(255, 235, 173, 1)" />
            <circle cx="1100" cy="300" r="24" stroke="#1D2667" strokeWidth="3" fill="rgba(187, 249, 197, 1)" />
            <circle cx="1180" cy="340" r="26" stroke="#1D2667" strokeWidth="3" fill="rgba(225, 243, 250, 1)" />

            {/* Dark blue node 3 - Interactive (secret) */}
            <circle
                cx="320"
                cy="280"
                r="26"
                stroke="#1D2667"
                strokeWidth="3"
                fill="rgba(0, 83, 166, 1)"
                onClick={() => handleNodeClick('node-320-280')}
                style={{ cursor: 'pointer' }}
            />

            <circle cx="520" cy="150" r="24" stroke="#1D2667" strokeWidth="3" fill="rgba(255, 235, 173, 1)" />

            {/* Easter egg: Animated train (clipped by circle) */}
            {allNodesClicked && (
                <g clipPath="url(#mask)" key={animationKey}>
                    <g transform="translate(0, 580)">
                        <g
                            style={{
                                animation: 'trainMove 4s linear forwards',
                            }}
                        >
                            {/* Train locomotive */}
                            <g transform="scale(-1, 1) translate(-120, 0)">
                                {/* Main body */}
                                <rect x="0" y="-45" width="120" height="40" rx="5" fill="#0053a6" />
                                {/* Cabin */}
                                <rect x="70" y="-70" width="45" height="25" rx="3" fill="#1e2869" />
                                {/* Cabin window */}
                                <rect x="80" y="-65" width="25" height="15" rx="2" fill="#EAF3FF" />
                                {/* Chimney */}
                                <rect x="15" y="-65" width="15" height="20" rx="2" fill="#1e2869" />
                                {/* Smoke puffs */}
                                <circle cx="22" cy="-80" r="8" fill="#ccc" opacity="0.7">
                                    <animate attributeName="cy" values="-80;-110" dur="1s" repeatCount="indefinite" />
                                    <animate attributeName="opacity" values="0.7;0" dur="1s" repeatCount="indefinite" />
                                </circle>
                                <circle cx="22" cy="-90" r="6" fill="#ddd" opacity="0.5">
                                    <animate attributeName="cy" values="-90;-120" dur="1.2s" repeatCount="indefinite" />
                                    <animate attributeName="opacity" values="0.5;0" dur="1.2s" repeatCount="indefinite" />
                                </circle>
                                {/* Front detail */}
                                <rect x="-10" y="-40" width="15" height="30" rx="3" fill="#1e2869" />
                                {/* Wheels */}
                                <circle cx="25" cy="-5" r="14" fill="#1e2869" stroke="#0D2E57" strokeWidth="3" />
                                <circle cx="25" cy="-5" r="5" fill="#EAF3FF" />
                                <circle cx="70" cy="-5" r="14" fill="#1e2869" stroke="#0D2E57" strokeWidth="3" />
                                <circle cx="70" cy="-5" r="5" fill="#EAF3FF" />
                                <circle cx="105" cy="-5" r="10" fill="#1e2869" stroke="#0D2E57" strokeWidth="3" />
                                <circle cx="105" cy="-5" r="4" fill="#EAF3FF" />
                            </g>
                            {/* Train car 1 */}
                            <g transform="translate(-90, 0)">
                                <rect x="0" y="-40" width="80" height="35" rx="4" fill="#377BD4" />
                                <rect x="8" y="-35" width="20" height="18" rx="2" fill="#EAF3FF" />
                                <rect x="35" y="-35" width="20" height="18" rx="2" fill="#EAF3FF" />
                                <rect x="62" y="-35" width="12" height="18" rx="2" fill="#EAF3FF" />
                                <circle cx="20" cy="-5" r="10" fill="#1e2869" stroke="#0D2E57" strokeWidth="3" />
                                <circle cx="60" cy="-5" r="10" fill="#1e2869" stroke="#0D2E57" strokeWidth="3" />
                            </g>
                            {/* Train car 2 */}
                            <g transform="translate(-180, 0)">
                                <rect x="0" y="-40" width="80" height="35" rx="4" fill="#377BD4" />
                                <rect x="8" y="-35" width="20" height="18" rx="2" fill="#EAF3FF" />
                                <rect x="35" y="-35" width="20" height="18" rx="2" fill="#EAF3FF" />
                                <rect x="62" y="-35" width="12" height="18" rx="2" fill="#EAF3FF" />
                                <circle cx="20" cy="-5" r="10" fill="#1e2869" stroke="#0D2E57" strokeWidth="3" />
                                <circle cx="60" cy="-5" r="10" fill="#1e2869" stroke="#0D2E57" strokeWidth="3" />
                            </g>
                        </g>
                    </g>
                </g>
            )}

            {/* CSS keyframes for train animation */}
            <style>
                {`
          @keyframes trainMove {
            0% { transform: translateX(-200px); }
            100% { transform: translateX(1600px); }
          }
        `}
            </style>
        </svg>
    );
};
